import { createServerFn } from '@tanstack/react-start'

import { getPlans } from '@/db/repositories/plans'
import { stripe } from '@/lib/stripe'
import {
  PLAN_CONFIG,
  PLAN_ORDER,
  type BillingCycle,
  type CompliassistPlan,
  type CompliassistPlanId,
} from '@/features/compliassist-pricing/data/plans'

// Amounts live in Stripe (single source of truth). Cache the assembled plans in
// memory so a public pricing page render doesn't hit Stripe/DB every time.
// ponytail: 5-min TTL memo; swap for a webhook-driven store only if prices need
// to update faster than that.
const TTL_MS = 5 * 60 * 1000
let cache: { at: number; plans: CompliassistPlan[] } | null = null

/** Full annual price (interval=year) is stored as unit_amount; the card shows a
    per-month equivalent. */
function monthlyEquivalent(annualUnitAmount: number): number {
  return Math.round(annualUnitAmount / 12)
}

async function assemblePlans(): Promise<CompliassistPlan[]> {
  const rows = await getPlans()
  const byType = new Map(rows.map((r) => [r.type as CompliassistPlanId, r]))

  const plans = await Promise.all(
    PLAN_ORDER.map(async (id): Promise<CompliassistPlan | null> => {
      const row = byType.get(id)
      if (!row) return null

      const [monthly, yearly] = await Promise.all([
        stripe.prices.retrieve(row.stripePriceIdMonthly),
        stripe.prices.retrieve(row.stripePriceIdYearly),
      ])

      return {
        id,
        ...PLAN_CONFIG[id],
        priceHtCents: {
          monthly: monthly.unit_amount ?? 0,
          yearly: monthlyEquivalent(yearly.unit_amount ?? 0),
        } as Record<BillingCycle, number>,
        stripePriceId: {
          monthly: row.stripePriceIdMonthly,
          yearly: row.stripePriceIdYearly,
        },
      }
    }),
  )

  return plans.filter((p): p is CompliassistPlan => p !== null)
}

export const getPricingPlans = createServerFn({ method: 'GET' }).handler(
  async () => {
    if (cache && Date.now() - cache.at < TTL_MS) return cache.plans
    const plans = await assemblePlans()
    cache = { at: Date.now(), plans }
    return plans
  },
)
